import { createApi } from '@reduxjs/toolkit/query/react'
import { DynamicBaseQueryHeader } from './DynamicBaseQueryHeader'
import type {
  ExpenseAnomalyFilter,
  ExpenseAnomalyLineItemsResult,
  ExpenseAnomalyLookups,
  ExpenseAnomalySchedule,
  ExpenseAnomalySummary,
  ExpenseAnomalyUserDetail,
  ExpenseAnomalyUserList,
} from '../app/pages/expenseAnomalies/types'

export const ExpenseAnomalyApi = createApi({
  reducerPath: 'ExpenseAnomalyApi',
  baseQuery: DynamicBaseQueryHeader('ExpenseAnomalies'),
  tagTypes: ['ExpenseAnomalyUsers', 'ExpenseAnomalySummary', 'ExpenseAnomalyLookups'],
  endpoints: (builder) => ({
    getLookups: builder.query<{ result: ExpenseAnomalyLookups; success: boolean }, void>({
      query: () => ({
        url: '/lookups',
        method: 'GET',
      }),
      providesTags: ['ExpenseAnomalyLookups'],
    }),
    getSummary: builder.query<
      { result: ExpenseAnomalySummary; success: boolean },
      ExpenseAnomalyFilter
    >({
      query: (body) => ({
        url: '/summary',
        method: 'POST',
        body,
      }),
      providesTags: ['ExpenseAnomalySummary'],
    }),
    getUsers: builder.query<
      { result: ExpenseAnomalyUserList; success: boolean },
      ExpenseAnomalyFilter
    >({
      query: (body) => ({
        url: '/users',
        method: 'POST',
        body,
      }),
      providesTags: ['ExpenseAnomalyUsers'],
    }),
    getUserDetail: builder.query<
      { result: ExpenseAnomalyUserDetail; success: boolean },
      { userAccountId: number; filter: ExpenseAnomalyFilter }
    >({
      query: ({ userAccountId, filter }) => ({
        url: `/users/${userAccountId}/detail`,
        method: 'POST',
        body: filter,
      }),
    }),
    getLineItems: builder.query<
      { result: ExpenseAnomalyLineItemsResult; success: boolean },
      {
        userAccountId: number
        categoryId: number
        year: number
        month: number
        pageNumber?: number
        pageSize?: number
      }
    >({
      query: ({ userAccountId, categoryId, year, month, pageNumber = 1, pageSize = 10 }) => ({
        url: `/users/${userAccountId}/categories/${categoryId}/months/${year}/${month}/line-items?pageNumber=${pageNumber}&pageSize=${pageSize}`,
        method: 'GET',
      }),
    }),
    exportCsv: builder.mutation<Blob, ExpenseAnomalyFilter>({
      query: (body) => ({
        url: '/export/csv',
        method: 'POST',
        body,
        responseHandler: async (response: Response) => response.blob(),
      }),
    }),
    getSchedule: builder.query<{ result: ExpenseAnomalySchedule; success: boolean }, void>({
      query: () => ({
        url: '/schedule',
        method: 'GET',
      }),
    }),
    calculateNow: builder.mutation<{ result: number; success: boolean; message?: string }, void>({
      query: () => ({
        url: '/calculate',
        method: 'POST',
      }),
      invalidatesTags: ['ExpenseAnomalyUsers', 'ExpenseAnomalySummary'],
    }),
  }),
})

export const {
  useGetLookupsQuery,
  useGetSummaryQuery,
  useGetUsersQuery,
  useGetUserDetailQuery,
  useGetLineItemsQuery,
  useExportCsvMutation,
  useGetScheduleQuery,
  useLazyGetScheduleQuery,
  useCalculateNowMutation,
} = ExpenseAnomalyApi
